// @flow
// $FlowFixMe
import React, { Fragment } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Datalayer } from '@finect/front-gtm';
import { X } from '@finect/tabular-icons/x';
import { maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { disableBodyScroll, enableBodyScroll } from '@finect/tabular-utils/bodyScrollLock';
import { Button } from '@finect/tabular-core/Button';
import { getFont } from '@finect/tabular-utils/getFont';
import { setPlusPromotion } from '../../utils/ga4Plus';

const show = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const animation = css`
  animation: ${show} 0.2s ease;
`;

const OverBkg = styled.div`
  position: fixed;
  z-index: 300;

  height: 100%;
  width: 100%;
  bottom: 0;
  background: linear-gradient(to top, #fff, rgba(255, 255, 255, 0.2));

  ${animation};

  ${maxMediaQueries.phone`
    background: linear-gradient(to top, #fff, rgba(255, 255, 255, 0.5));
  `}
`;

const CloseOverlay = styled.div`
  position: absolute;
  z-index: 300;
  top: 20px;
  right: 20px;
  cursor: pointer;
  color: white;

  ${maxMediaQueries.phone`
    top: 15px;
    right: 15px;
  `};
`;

const ContentBox = styled.div`
  position: fixed;
  z-index: 300;

  height: auto;
  min-width: 300px;
  width: 80%;
  max-width: 800px;
  bottom: 40%;
  left: 50%;
  transform: translateX(-50%);

  border-radius: 12px;
  background: ${({ theme }) => theme.plus.background};
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${getFont(1)}
  font-weight: 400;

  padding: 24px 16px 0;
  ${animation};

  p {
    width: 100%;
  }

  ${Button} {
    width: 100%;
    max-width: 300px;
    border-radius: 8px;
    padding-top: 6px;
    padding-bottom: 6px;
    margin-top: 24px;
    margin-bottom: 24px;
  }

  ${maxMediaQueries.phone`
    height: auto;
    width: 90%;
    padding: 36px 14px 14px;
    
    ${Button} {
     max-width: 100%;
    }
  `}
`;

const TYPES_WITH_BLOCKED_SCREEN = ['comparison', 'comparison-daily'];

const TEXT_TYPES = {
  product: 'productos favoritos',
  article: 'artículos favoritos',
  comparison: 'comparaciones guardadas',
  'comparison-daily': 'comparaciones diarias'
};

const EXTRA_BY_TYPE = {
  product: 'guardar favoritos ilimitados',
  article: 'guardar favoritos ilimitados',
  comparison: 'guardar comparaciones ilimitadas',
  'comparison-daily': 'hacer comparaciones ilimitadas'
};

const TEXT_PERIODS = {
  month: 'mensual'
};

const PLUS_ADVANTAGES = [
  { types: ['comparison', 'comparison-daily'], text: 'Comparaciones sin límite' },
  { types: ['product', 'article'], text: 'Guardar favoritos ilimitados' },
  { types: ['tools'], text: 'Herramientas financieras avanzadas' },
  { types: ['portfolio'], text: 'Mi Cartera' },
  { types: ['trends'], text: 'Finect Trends' }
];

const AdvantagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
`;

const AdvantagesContent = styled.div`
  text-align: left;
  width: 100%;
  max-width: 300px;

  > span {
    display: block;
    margin-bottom: 4px;
    font-weight: 600;
    ${getFont(0)}
    color: #fff;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      margin-bottom: 4px;
      padding-left: 20px;
      position: relative;
      ${getFont(0)}
      line-height: 1.4;
      color: rgba(255, 255, 255, 0.9);

      &::before {
        content: '•';
        position: absolute;
        left: 0;
        top: 0;
        font-weight: bold;
        color: #fff;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

type Props = {
  url: string,
  data: Object,
  closePlusOverlay: Function
};

export class PlusOverBox extends React.Component<Props> {
  target: HTMLElement | null;
  state = { currentHref: '' };

  componentDidMount() {
    this.setState({ currentHref: window.location.href });
    if (TYPES_WITH_BLOCKED_SCREEN.includes(this.props.data.type)) {
      disableBodyScroll(this.target);
    }
  }

  componentWillUnmount() {
    if (TYPES_WITH_BLOCKED_SCREEN.includes(this.props.data.type)) {
      enableBodyScroll(this.target);
    }
  }

  handlePlusClick = () => {
    setPlusPromotion('plus-btnLimitacionModal', 'finect_plus');
    this.props.closePlusOverlay();
  };

  render() {
    const { url, data, closePlusOverlay } = this.props;

    // param back to current url
    const urlWithBack = `${url}?back=${encodeURIComponent(this.state.currentHref)}`;

    const period = TEXT_PERIODS[data.period];
    const extra = EXTRA_BY_TYPE[data.type];

    return (
      <Fragment>
        <Datalayer
          id="Impression"
          type="fiEvent"
          params={{
            category: 'LimitationModalImpression',
            action: data.type,
            label: 'plus-limit-modal'
          }}
        />
        <OverBkg ref={el => (this.target = el)} onClick={closePlusOverlay} />
        <ContentBox>
          <p style={{ marginBottom: 16 }}>
            ¡Vaya! Has alcanzado tu límite {period ? `${period} ` : ''}de {TEXT_TYPES[data.type]}.
          </p>
          <p>
            ✅ Accede a <b>Finect Plus</b> para {extra ? `${extra}.` : ''}
          </p>

          <Button
            data-fi-event="plus-btnLimitacionModal"
            as={Link}
            to={urlWithBack}
            color="secondary"
            onClick={this.handlePlusClick}
          >
            Descubre <b>Finect Plus</b>
          </Button>

          <AdvantagesContainer>
            <AdvantagesContent>
              <span>También incluye:</span>
              <ul>
                {PLUS_ADVANTAGES.filter(adv => !adv.types.includes(data.type)).map((adv, i) => (
                  <li key={i}>{adv.text}</li>
                ))}
              </ul>
            </AdvantagesContent>
          </AdvantagesContainer>

          <CloseOverlay onClick={closePlusOverlay}>
            <X />
          </CloseOverlay>
        </ContentBox>
      </Fragment>
    );
  }
}
